import { createContext, useContext, useEffect, useRef } from 'react';
import { createStore, useStore } from 'zustand';

export interface LiveMomentState {
  showTrends: boolean;
  isQrCodeSidebarVisible: boolean | undefined;
}

interface LiveMomentStore extends LiveMomentState {
  setIsQrCodeSidebarVisible: (isVisible: boolean | undefined) => void;
}

const initialState: LiveMomentState = {
  showTrends: false,
  isQrCodeSidebarVisible: undefined,
};

const createLiveMomentStore = (initialProps: Partial<LiveMomentState>) => {
  return createStore<LiveMomentStore>((set) => ({
    ...initialState,
    ...initialProps,
    setIsQrCodeSidebarVisible: (isQrCodeSidebarVisible) =>
      set({ isQrCodeSidebarVisible }),
  }));
};

type LiveMomentStoreContextType = ReturnType<typeof createLiveMomentStore>;

const LiveMomentStoreContext = createContext<
  LiveMomentStoreContextType | undefined
>(undefined);

export function useLiveMomentStore<T>(
  selector: (state: LiveMomentStore) => T,
): T {
  const store = useContext(LiveMomentStoreContext);
  if (!store) {
    throw new Error('SfinxError: no live moment store found');
  }

  return useStore(store, selector);
}

export const LiveMomentStoreProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value?: Partial<LiveMomentState>;
}) => {
  const store = useRef<LiveMomentStoreContextType>();
  if (!store.current) {
    store.current = createLiveMomentStore(value || {});
  }

  useEffect(() => {
    store.current?.setState((prev) => ({ ...prev, ...value }));
  }, [value]);

  return (
    <LiveMomentStoreContext.Provider value={store.current}>
      {children}
    </LiveMomentStoreContext.Provider>
  );
};
